import type { AISettings, AIClient } from '$lib/types/ai';
import { StreamingClientFactory } from '../streaming/StreamingClientFactory';

/**
 * AI Client Manager Service
 * Manages AI client lifecycle, initialization, and configuration
 * Single Responsibility: Client management and lifecycle control
 */
export class AIClientManagerService {
  private aiClient: AIClient | null = null;
  private settings: AISettings;
  private isPremium: boolean = false;

  constructor(settings: AISettings) {
    this.settings = settings;
  }

  /**
   * Initialize the AI client with current settings
   */
  async initializeClient(): Promise<void> {
    try {
      // Enhanced settings with premium status
      const enhancedSettings = {
        ...this.settings,
        isPremium: this.isPremium
      };
      
      
      // Dynamic import to avoid circular dependencies
      const { AIClientFactory } = await import('../AIClientFactory');
      this.aiClient = AIClientFactory.createClient(enhancedSettings);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      console.error('[AIClientManagerService] Failed to initialize:', errorMessage);
      throw new Error(`Failed to initialize AI client: ${errorMessage}`);
    }
  }

  /**
   * Initialize streaming client with optional fallback models
   */
  async initializeStreamingClient(fallbackModels?: string[]): Promise<void> {
    try {
      const streamingClient = StreamingClientFactory.createStreamingClient(
        this.settings,
        fallbackModels ? 
          StreamingClientFactory.createModelFallbackConfig(this.settings, fallbackModels) :
          undefined
      );

      this.aiClient = streamingClient;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      throw new Error(`Failed to initialize streaming client: ${errorMessage}`);
    }
  }

  /**
   * Get current AI client
   */
  getClient(): AIClient | null {
    return this.aiClient;
  }

  /**
   * Check if client is initialized and available
   */
  async isClientAvailable(): Promise<boolean> {
    if (!this.aiClient) {
      return false;
    }
    
    try {
      const available = await Promise.resolve(this.aiClient.isAvailable());
      return available;
    } catch {
      return false;
    }
  }

  /**
   * Get current model name
   */
  getModelName(): string {
    return this.aiClient?.getModelName() || 'Unknown';
  }

  /**
   * Check if client supports streaming
   */
  supportsStreaming(): boolean {
    return this.aiClient?.supportsStreaming?.() || false;
  }

  /**
   * Update settings and reset client for reinitialization
   */
  updateSettings(settings: AISettings): void {
    this.settings = settings;
    this.aiClient = null; // Will be reinitialized on next request
  }

  /**
   * Set premium status
   */
  setPremiumStatus(isPremium: boolean): void {
    this.isPremium = isPremium;
  }

  /**
   * Reset client state
   */
  reset(): void {
    this.aiClient = null;
  }

  /**
   * Ensure client is initialized
   */
  async ensureClientInitialized(): Promise<void> {
    if (!this.aiClient) {
      await this.initializeClient();
    }

    const isAvailable = await this.isClientAvailable();
    if (!isAvailable) {
      throw new Error(`AI client is not available (provider: ${this.settings.provider}, model: ${this.settings.selectedModel})`);
    }
  }

  /**
   * Get client configuration summary
   */
  getClientInfo(): {
    isInitialized: boolean;
    modelName: string;
    provider: string;
    supportsStreaming: boolean;
  } {
    return {
      isInitialized: this.aiClient !== null,
      modelName: this.getModelName(),
      provider: this.settings.provider,
      supportsStreaming: this.supportsStreaming()
    };
  }
}